import { useState, useEffect } from 'react'
import { Workspace } from 'types'
import { MY_ACCOUNT_ITEMS, DANGER_ZONE_ITEMS } from '../utils'
import { isAlmanacCoreWorkspace } from 'utils/workspaceUtils'
import { UserClient } from 'api/clients/UserClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import useIsMounted from 'ismounted'
import { useWorkspaceNavItems } from '../hooks/useWorkspaceNavItems'
import { Modal } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { SettingScreen } from '../types'
import { IntegrationsDrawer } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/IntegrationsDrawer'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import Styles from './SettingsSideNav.module.scss'

interface Props {
  workspace: Workspace
  selectedItem: string
  handleClick: (key: string) => void
}

const SettingsSideNav = observer(({ workspace, selectedItem, handleClick }: Props) => {
  const notInCore = !isAlmanacCoreWorkspace(workspace.id)
  const [canSeeWorkspaceSettings, setCanSeeWorkspaceSettings] = useState(notInCore)
  const isMounted = useIsMounted()
  const isVirtualMarketer = workspace.is_virtual_marketer
  const { workspaceNavItems } = useWorkspaceNavItems(workspace)
  const { isMobile } = useResponsiveDesign()
  const { drawersStore } = useRootStore()

  useEffect(() => {
    if (notInCore) return

    const fetchIsAlmanacPrivateMember = async () => {
      const response = await UserClient.getIsAlmanacPrivateMember()
      if (response && response.type === SUCCESS_RESPONSE_TYPE) {
        const {
          data: { almanac_private_member },
        } = response
        if (isMounted.current) setCanSeeWorkspaceSettings((prev) => prev || almanac_private_member)
      }
    }
    fetchIsAlmanacPrivateMember()
  }, [notInCore, isMounted])

  const nonCore = (x: { key: string }) => canSeeWorkspaceSettings || x.key !== 'profile_directory'
  const noVirtualMarketerMembershipCard = (x: { key: string }) =>
    !isVirtualMarketer || x.key !== SettingScreen.MembershipCard

  return (
    <>
      <Modal.ListSection title="My Account">
        {MY_ACCOUNT_ITEMS.filter(nonCore)
          .filter(noVirtualMarketerMembershipCard)
          .map((item) => (
            <Modal.ListItem
              itemLabel={item.label}
              key={item.key}
              itemKey={item.key}
              data-heap-id={`settings-side-nav-${item.key}`}
              isSelected={selectedItem === item.key}
              onClick={() => handleClick(item.key)}
            />
          ))}
      </Modal.ListSection>
      {canSeeWorkspaceSettings && (
        <Modal.ListSection title={workspace.name} data-testid="settings-side-nav-workspace-name">
          {workspaceNavItems.map((item) => (
            <>
              {isMobile && item.key === SettingScreen.Integrations ? (
                <Modal.ListItem
                  itemLabel={item.label}
                  key={item.key}
                  itemKey={item.key}
                  data-heap-id={`settings-side-nav-${item.key}`}
                  isSelected={selectedItem === item.key}
                  onClick={() => drawersStore.openDrawer(IntegrationsDrawer)}
                />
              ) : (
                <Modal.ListItem
                  itemLabel={item.label}
                  key={item.key}
                  itemKey={item.key}
                  data-heap-id={`settings-side-nav-${item.key}`}
                  isSelected={selectedItem === item.key}
                  onClick={() => handleClick(item.key)}
                />
              )}
            </>
          ))}
        </Modal.ListSection>
      )}
      <Modal.ListSection>
        {DANGER_ZONE_ITEMS.map((item) => (
          <Modal.ListItem
            itemLabel={item.label}
            key={item.key}
            itemKey={item.key}
            data-heap-id={`settings-side-nav-${item.key}`}
            isSelected={selectedItem === item.key}
            onClick={() => handleClick(item.key)}
          />
        ))}
      </Modal.ListSection>
    </>
  )
})

export default SettingsSideNav
